import cx from "classnames";
import { useSetAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { canv } from "@/canv/global";
import { frameWidget } from "@/elements/frame";
import { PresentationWidget } from "@/elements/Presentation/Presentation";
import { Group } from "@/group";
import { headerActiveButtonAtom } from "@/interface/AppStore";
import { AddFirstPresentation } from "./AddFirstPresentation";
import { PresentationHeader } from "./PresentationHeader";
import { PresentationsNavigator } from "./PresentationsNavigator";
import type { PresentationData } from "./PresentationsNavigator/types";
import styles from "./PresentationsWindow.module.scss";

export const PresentationsWindow = () => {
    const [hasPresentations, setHasPresentations] = useState(false);
    const setActiveHeaderButton = useSetAtom(headerActiveButtonAtom);

    const [viewedPresentation, setViewedPresentation] = useState<PresentationData | null>(null);
    const [presentations, setPresentations] = useState<PresentationData[]>([]);

    const handleClose = useCallback(() => {
        setActiveHeaderButton(null);
    }, [setActiveHeaderButton]);

    const handleBackToList = useCallback(() => {
        setViewedPresentation(null);
    }, []);

    const getAllPresentations = useCallback((): PresentationData[] => {
        const result: PresentationData[] = [];
        const frameMap = new Map<string, frameWidget>();

        canv.canvasController.items.forEach((el) => {
            if (
                el instanceof Group &&
                el.isItemWrapper() &&
                el.children[0] instanceof frameWidget
            ) {
                frameMap.set(el.UID, el.children[0]);
            }
        });

        canv.canvasController.items.forEach((group) => {
            if (
                group instanceof Group &&
                group.isItemWrapper() &&
                group.children[0] instanceof PresentationWidget
            ) {
                const presentation = group.children[0] as PresentationWidget;

                const frames = presentation.slides
                    .map((sl) => frameMap.get(sl.frameUID))
                    .filter(Boolean) as frameWidget[];

                result.push({
                    UID: presentation.UID,
                    title: presentation.name || "Нет названия",
                    date: new Date(presentation.updatedAt || presentation.createdAt),
                    actor: presentation.accountName,
                    frames,
                });
            }
        });

        return result;
    }, []);

    const refreshPresentations = useCallback(() => {
        const result = getAllPresentations();
        setPresentations(result);

        setViewedPresentation((prev) => {
            if (!prev && result.length !== 1) return prev;

            if (result.length === 1) {
                return result[0];
            }

            if (prev) {
                const exists = result.find((p) => p.UID === prev.UID);

                return exists || null;
            }
            return null;
        });

        setHasPresentations(!!result.length);
    }, [getAllPresentations]);

    const handleSetViewedPresentation = (data: PresentationData | null) => {
        setViewedPresentation(data);
        if (data) {
            const item = canv.canvasController.FindByUID(data.UID);
            if (item) {
                canv.zoomToSelected([item]);
            }
        }
    };

    useEffect(() => {
        refreshPresentations();

        document.addEventListener("updatePresentationWindowMessage", refreshPresentations);
        return () => {
            document.removeEventListener("updatePresentationWindowMessage", refreshPresentations);
        };
    }, [refreshPresentations]);

    return (
        <div className={styles.slidesWrapper}>
            <PresentationHeader
                presentation={!!viewedPresentation}
                onBack={handleBackToList}
                onClose={handleClose}
                hideBack={presentations.length === 1}
            />

            <div className={cx(styles.slidesContent, "scrollable")}>
                {hasPresentations ? (
                    <PresentationsNavigator
                        presentation={viewedPresentation}
                        setPresentation={handleSetViewedPresentation}
                        presentations={presentations}
                    />
                ) : (
                    <AddFirstPresentation />
                )}
            </div>
        </div>
    );
};
